import { useState, type JSX } from 'react';
import { Mail, Loader2, AlertCircle, Check, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { HighlightBox } from '@/components/shared/HighlightBox';
import { useTruncationObserver } from '@/hooks/useTruncationObserver';
import {
  useInvitationResponse,
  useRespondToInviteByToken,
} from '@/hooks/api/useOrganizations';
import { InviteAction } from '@kirin/api-client/contracts/organizations';
import { ApiError } from '@kirin/api-client/errors';
import { isTerminalInviteError } from '@/lib/inviteErrors';

interface InviteAcceptModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: string;
  organizationName: string;
  /**
   * When provided, accept/reject are driven by the invite token (KIR-3786) —
   * the token, not the typed email, decides the org. Used by the deep-link flow.
   */
  token?: string;
  /**
   * When true, the modal cannot be dismissed without responding: the user has an
   * open invite and personal accounts are locked out until they accept or reject.
   *
   * This is lifted automatically once the invite turns out to be permanently
   * unanswerable — see `isTerminalInviteError` (KIR-4166).
   */
  mandatory?: boolean;
  /**
   * Set when the invite is known *up front* to be unanswerable by the signed-in
   * identity (the resolve call reported `canRespond: false`). Renders the
   * dead-end state immediately — message plus Dismiss — instead of offering
   * Accept/Reject that are guaranteed to 403 (KIR-4166).
   */
  unavailableReason?: string | null;
}

function OrganizationNameDisplay({ name }: { name: string }): JSX.Element {
  const { truncationRef, isTruncated, checkTruncation } =
    useTruncationObserver();

  const nameSpan = (
    <span
      ref={truncationRef}
      tabIndex={isTruncated ? 0 : undefined}
      className={
        isTruncated
          ? 'block truncate font-semibold text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm'
          : 'block truncate font-semibold text-base text-foreground'
      }
      onMouseEnter={checkTruncation}
      onFocus={isTruncated ? checkTruncation : undefined}
    >
      {name}
    </span>
  );

  return (
    <div className="min-w-0">
      {isTruncated ? (
        <Tooltip>
          <TooltipTrigger asChild>{nameSpan}</TooltipTrigger>
          <TooltipContent side="top" className="break-words">
            {name}
          </TooltipContent>
        </Tooltip>
      ) : (
        nameSpan
      )}
    </div>
  );
}

export function InviteAcceptModal({
  open,
  onOpenChange,
  organizationId,
  organizationName,
  token,
  mandatory = false,
  unavailableReason = null,
}: InviteAcceptModalProps) {
  const [pendingAction, setPendingAction] = useState<InviteAction | null>(null);
  const orgResponse = useInvitationResponse();
  const tokenResponse = useRespondToInviteByToken();

  // Token flow is the source of truth when a token is present (deep link).
  const { isPending, error } = token ? tokenResponse : orgResponse;

  // A permanently-unanswerable invite: both accept and reject will keep failing
  // (e.g. the invite was addressed to a `user+tag@` subaddress Descope treats as
  // a separate identity). Known either up front from the resolve call, or after
  // a terminal response error. Keeping the modal mandatory here would block
  // every page with no way out, so we unlock it and offer dismissal instead
  // (KIR-4166).
  //
  // Deliberately has NO side effect: an earlier version cleared the persisted
  // token as soon as the dead end was detected, which destroyed the very state
  // that keeps this modal mounted. Under StrictMode's double-mount the token was
  // gone before the second render, so the message never appeared at all. The
  // token is now dropped only when the user actually dismisses (KIR-4166).
  const isDeadEnd = Boolean(unavailableReason) || isTerminalInviteError(error);

  const canDismiss = !mandatory || isDeadEnd;

  const respond = async (action: InviteAction): Promise<void> => {
    if (token) {
      await tokenResponse.mutateAsync({ token, action });
    } else {
      await orgResponse.mutateAsync({ organizationId, action });
    }
  };

  const handleAccept = async () => {
    setPendingAction(InviteAction.Accept);
    try {
      await respond(InviteAction.Accept);
      // Navigation and page reload are handled by the mutation
      // The mutation will reload the page to the accepted organization
    } catch (err) {
      // Error is already handled by the mutation and stored in the error state
      console.error('Failed to accept invite:', err);
      setPendingAction(null);
    }
  };

  const handleReject = async () => {
    setPendingAction(InviteAction.Reject);
    try {
      await respond(InviteAction.Reject);
      // Close modal after successful rejection (handled by onOpenChange)
      onOpenChange(false);
    } catch (err) {
      // Error is already handled by the mutation and stored in the error state
      console.error('Failed to reject invite:', err);
      setPendingAction(null);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    // Mandatory invites cannot be dismissed by clicking away — the user must
    // explicitly accept or reject (KIR-3786) — unless the invite has become
    // permanently unanswerable, in which case there is nothing to respond to
    // and blocking would trap the user on every page (KIR-4166).
    if (!canDismiss) {
      return;
    }
    if (!isPending) {
      // Only allow closing when not pending
      if (!newOpen) {
        // Reset pending action when closing
        setPendingAction(null);
      }
      onOpenChange(newOpen);
    }
  };

  const getErrorMessage = (error: unknown): string => {
    if (error instanceof ApiError) {
      // Handle specific error types
      if (error.status === 404) {
        return 'This invitation is no longer available.';
      }
      // Only a *definitive* 403 is an identity mismatch. A recoverable one
      // (INVITE_IDENTITY_UNVERIFIED) keeps Accept/Reject and offers no Dismiss
      // button, so this copy would name an action that isn't on screen and
      // misdescribe the cause. Falls through to the server's own
      // verify-and-retry message below. Shares `isTerminalInviteError` with the
      // button state so wording and controls can't disagree (KIR-4166).
      if (error.status === 403 && isTerminalInviteError(error)) {
        // Accept and reject are both identity-bound (KIR-3786): the signed-in
        // identity must be the invited one. Point at the way out rather than at
        // "decline", which is equally blocked (KIR-4166).
        return 'This invitation was sent to a different email address than the one you are signed in with. Sign in with the invited address to respond to it, or dismiss this invitation.';
      }
      if (error.status === 400) {
        // Terminal (KIR-4166) — retrying an unparseable invite never succeeds.
        return 'This invitation link is not valid.';
      }
      if (
        error.data &&
        typeof error.data === 'object' &&
        'message' in error.data
      ) {
        return String(error.data.message);
      }
    }
    return 'Failed to process invitation. Please try again.';
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent showCloseButton={!isPending && canDismiss}>
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 flex-shrink-0" />
            <DialogTitle className="leading-normal pb-0.5">
              Organization Invitation
            </DialogTitle>
          </div>
          <DialogDescription>
            You have been invited to join the following organization:
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 py-2">
          <OrganizationNameDisplay name={organizationName} />
          {!isDeadEnd && (
            <p className="text-muted-foreground text-sm">
              Would you like to accept or reject this invitation?
            </p>
          )}
          {(unavailableReason || error) && (
            <HighlightBox
              variant="error"
              radius="md"
              className="flex items-start gap-2 text-sm"
              role="alert"
            >
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{unavailableReason ?? getErrorMessage(error)}</span>
            </HighlightBox>
          )}
        </div>

        <DialogFooter>
          {isDeadEnd ? (
            // Nothing left to accept or reject — the only useful action is to
            // get the (otherwise page-blocking) modal out of the way (KIR-4166).
            <Button type="button" onClick={() => onOpenChange(false)}>
              <X className="h-4 w-4" />
              Dismiss
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={handleReject}
                disabled={isPending}
              >
                {pendingAction === InviteAction.Reject ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Rejecting...
                  </>
                ) : (
                  <>
                    <X className="h-4 w-4" />
                    Reject
                  </>
                )}
              </Button>
              <Button type="button" onClick={handleAccept} disabled={isPending}>
                {pendingAction === InviteAction.Accept ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Accepting...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4" />
                    Accept
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
